import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  hasAllPermissions as checkAllPermissions,
} from '@/lib/permissions';

/**
 * Permission checking hook
 * Provides convenient methods for checking user permissions with wildcard support
 */
export function usePermissions() {
  const { permissions } = useAuth();

  /**
   * Check if the user has a specific permission
   * Supports wildcards:
   * - '*' matches every permission except the explicit-only ones
   * - 'resource:*' matches all actions on a resource
   * - 'resource:action' matches exact permission
   * - 'resource:action:scope' matches scoped permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      return checkPermission(permissions, permission);
    },
    [permissions]
  );

  /**
   * Check if the user has at least one of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (...requiredPermissions: string[]): boolean => {
      return checkAnyPermission(permissions, requiredPermissions);
    },
    [permissions]
  );

  /**
   * Check if the user has all of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (...requiredPermissions: string[]): boolean => {
      return checkAllPermissions(permissions, requiredPermissions);
    },
    [permissions]
  );

  /**
   * Get all permissions for a specific resource
   */
  const getResourcePermissions = useCallback(
    (resource: string): string[] => {
      return permissions.filter(
        (p) => p === '*' || p.startsWith(`${resource}:`)
      );
    },
    [permissions]
  );

  /**
   * Check if the user can perform an action on a resource with optional scope
   * @param resource - The resource (e.g., 'invoices')
   * @param action - The action (e.g., 'create', 'read', 'update', 'delete')
   * @param scope - Optional scope (e.g., 'own', 'all')
   */
  const can = useCallback(
    (resource: string, action: string, scope?: string): boolean => {
      const permission = scope
        ? `${resource}:${action}:${scope}`
        : `${resource}:${action}`;
      return hasPermission(permission);
    },
    [hasPermission]
  );

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getResourcePermissions,
    can,
  };
}

export default usePermissions;
